// Interfaţa de telefon: bară sus, meniu jos, restul pe tot ecranul.
//
// Nu e bara laterală strânsă. Pe telefon degetul mare ajunge comod în treimea de jos a
// ecranului, deci navigarea stă jos — aşa fac toate aplicaţiile pe care oamenii le
// folosesc zilnic, şi nu din obişnuinţă, ci fiindcă mâna e făcută aşa.
//
// Cinci locuri, nici unul mai mult: al şaselea ar face fiecare ţintă prea îngustă
// pentru un deget, iar ce nu încape aici încape în meniul de sub „☰". Butonul din
// mijloc e cel de publicat — ridicat şi colorat, fiindcă e singurul care **face** ceva,
// restul doar te duc undeva.
//
// Bara de sus se ascunde când derulezi în jos şi se întoarce când derulezi în sus:
// când citeşti, ecranul e al textului; când vrei să navighezi, mişcarea în sus e
// oricum primul lucru pe care îl faci.

import { el, clear, bus } from '../core/util.js';
import { icon } from './icons.js';
import { session } from '../core/session.js';
import { pictAvatar } from './avatars.js';
import { foaie } from './sheet.js';
import { alegeVersiune } from '../core/device.js';

/** Cele cinci locuri de jos. `actiune` în loc de `drum` înseamnă că nu duce nicăieri. */
const TABURI = [
  { id: 'feed', drum: '/feed/', eticheta: 'Лента', ico: 'comments' },
  { id: 'screener', drum: '/screener/coins/', eticheta: 'Скринер', ico: 'chartLine' },
  { id: 'compune', actiune: 'compune', eticheta: '', ico: 'plus', mijloc: true },
  { id: 'mesaje', drum: '/messages/', eticheta: 'Сообщения', ico: 'send', insigna: true },
  { id: 'profile', drum: '/profile/', eticheta: 'Профиль', ico: 'user' },
];

/** Ce intră în meniul de sub „☰". Restul platformei, grupat ca în bara laterală. */
const MENIU = [
  { titlu: 'Сообщество', randuri: [
    { eticheta: 'Участники', ico: 'users', drum: '/members/' },
    { eticheta: 'Общий чат', ico: 'comments', drum: '/chat/' },
    { eticheta: 'Торговые идеи', ico: 'lightbulb', drum: '/publications/' },
    { eticheta: 'Торговые отчёты', ico: 'clipboard', drum: '/reports/' },
    { eticheta: 'Лидеры рейтинга', ico: 'trophy', drum: '/leaderboard/' },
  ] },
  { titlu: 'Инструменты', randuri: [
    { eticheta: 'Дашборд', ico: 'dashboard', drum: '/' },
    { eticheta: 'Тренажёр', ico: 'clockHistory', drum: '/preplay/' },
    { eticheta: 'Стратегии', ico: 'book', drum: '/strategies/' },
  ] },
  { titlu: 'Аккаунт', randuri: [
    { eticheta: 'Настройки аккаунта', ico: 'userGear', drum: '/account/' },
    { eticheta: 'Модерация', ico: 'shieldCheck', drum: '/admin/', personal: true },
    { eticheta: 'О платформе', ico: 'info', drum: '/about/' },
    { eticheta: 'Правила', ico: 'book', drum: '/rules/' },
  ] },
];

export class MobileShell {
  constructor(app) {
    this.app = app;
    this.taburi = new Map();
  }

  mount() {
    this.titluEl = el('h1', { class: 'mb-titlu', text: 'ScalpScreener' });
    this.insignaEl = el('span', { class: 'mt-insigna', text: '0' });

    this.barăSus = el('header', { class: 'mt-sus' }, [
      el('button', { class: 'mt-btn', title: 'Меню', onclick: () => this.#meniu() },
        [icon('rows', 17)]),
      this.titluEl,
      el('button', { class: 'mt-btn', title: 'Поиск', onclick: () => this.app.globalSearch?.open() },
        [icon('search', 16)]),
      el('button', { class: 'mt-btn', title: 'Уведомления',
        onclick: () => this.app.deschideMesaje('mine') }, [icon('bell', 15)]),
    ]);

    this.viewEl = el('main', { class: 'mt-view' });

    this.barăJos = el('nav', { class: 'mt-jos' });
    for (const t of TABURI) {
      const b = el('button', {
        class: `mt-tab${t.mijloc ? ' mijloc' : ''}`,
        onclick: () => this.#apasa(t),
      }, [
        el('span', { class: 'mt-tab-ico' }, [icon(t.ico, t.mijloc ? 20 : 19)]),
        t.eticheta ? el('span', { class: 'mt-tab-t', text: t.eticheta }) : null,
        t.insigna ? this.insignaEl : null,
      ]);
      this.taburi.set(t.id, b);
      this.barăJos.append(b);
    }

    this.root = el('div', { class: 'mt-shell' }, [this.barăSus, this.viewEl, this.barăJos]);
    document.body.append(this.root);

    // Bara de sus se ascunde la derulare în jos. Pragul e mic, dar nu zero: fără el,
    // un deget care tremură ar face bara să pâlpâie.
    let ultima = 0;
    this.viewEl.addEventListener('scroll', () => {
      const y = this.viewEl.scrollTop;
      if (Math.abs(y - ultima) > 8) {
        this.root.classList.toggle('ascunsa', y > ultima && y > 60);
        ultima = y;
      }
    }, { passive: true });

    bus.on('session:change', () => this.#syncAvatar());
    this.#syncAvatar();
    return this;
  }

  /** Avatarul propriu ţine locul iconiţei de profil, ca peste tot pe telefon. */
  #syncAvatar() {
    const b = this.taburi.get('profile');
    if (!b || !session.user) return;
    const ico = b.querySelector('.mt-tab-ico');
    clear(ico);
    const av = el('span', { class: 'mt-av' });
    pictAvatar(av, session.user, 22);
    ico.append(av);
  }

  setTitle(text) { this.titluEl.textContent = text; }

  setBadge(n) {
    this.insignaEl.textContent = String(n);
    this.insignaEl.classList.toggle('are', n > 0);
  }

  setActive(drum) {
    for (const t of TABURI) {
      const b = this.taburi.get(t.id);
      const activ = t.drum && (t.drum === drum
        || (t.drum === '/screener/coins/' && drum.startsWith('/screener'))
        || (t.drum === '/messages/' && drum.startsWith('/messages'))
        || (t.drum === '/profile/' && drum.startsWith('/profile')));
      b.classList.toggle('activ', !!activ);
    }
  }

  #apasa(t) {
    if (t.drum) { this.app.navigate(t.drum); return; }
    if (t.actiune === 'compune') this.compune();
    if (t.actiune === 'alerte') this.app.openNotifications();
  }

  /** Caseta de publicat, ca foaie. Aceeaşi bucată ca pe calculator. */
  async compune() {
    const { FeedSection } = await import('../sections/feed.js');
    const lenta = new FeedSection(this.app);
    let inchideMa;
    const caseta = lenta.compunere(async () => {
      inchideMa?.();
      // Dacă tocmai eşti în lentă, o reîmprospătăm ca să-ţi vezi publicaţia sus.
      if (location.pathname === '/feed/') this.app.navigate('/feed/', { fortat: true });
      else this.app.navigate('/feed/');
    });
    ({ inchide: inchideMa } = foaie({ titlu: 'Новая публикация', corp: caseta, inalta: true }));
  }

  /** Lista de conversaţii. Pe telefon fereastra andocată n-are unde sta. */
  async #mesaje() {
    const corp = el('div', { class: 'mt-dm' }, [el('div', { class: 'fd-gol-mic', text: 'Загружаем…' })]);
    const { inchide } = foaie({ titlu: 'Сообщения', corp, inalta: true });
    try {
      const d = await (await fetch('/api/dm')).json();
      clear(corp);
      if (!d.threads?.length) {
        corp.append(el('div', { class: 'fd-gol', text: 'Переписок пока нет' }));
        return;
      }
      for (const t of d.threads) {
        const av = el('span', { class: 'fd-av mic' });
        pictAvatar(av, t.user, 34);
        corp.append(el('button', {
          class: 'mt-dm-rand',
          onclick: async () => {
            inchide();
            const { deschideMesaje } = await import('./messages.js');
            deschideMesaje(t.user.username);
          },
        }, [
          av,
          el('div', { class: 'mt-dm-corp' }, [
            el('span', { class: 'mt-dm-nume', text: t.user.username }),
            el('span', { class: 'mt-dm-ultim', text: t.last || '' }),
          ]),
          t.unread ? el('span', { class: 'mt-dm-necitit', text: String(t.unread) }) : null,
        ]));
      }
    } catch {
      clear(corp).append(el('div', { class: 'fd-gol', text: 'Не удалось загрузить' }));
    }
  }

  /** Restul platformei. */
  #meniu() {
    const corp = el('div', { class: 'mt-meniu' });
    let inchide;

    if (session.user) {
      const av = el('span', { class: 'fd-av' });
      pictAvatar(av, session.user, 44);
      corp.append(el('button', {
        class: 'mt-meniu-eu',
        onclick: () => { inchide(); this.app.navigate('/profile/'); },
      }, [
        av,
        el('div', {}, [
          el('span', { class: 'mt-meniu-nume', text: session.user.username }),
          el('span', { class: 'mt-meniu-rang', text: session.user.status?.name || '' }),
        ]),
        icon('chevronRight', 12),
      ]));
    }

    for (const grup of MENIU) {
      const randuri = grup.randuri.filter(
        (r) => !r.personal || ['owner', 'admin'].includes(session.user?.role));
      if (!randuri.length) continue;
      corp.append(el('div', { class: 'mt-meniu-titlu', text: grup.titlu }));
      for (const r of randuri) {
        corp.append(el('button', {
          class: 'mt-meniu-rand',
          onclick: () => { inchide(); this.app.navigate(r.drum); },
        }, [icon(r.ico, 15), el('span', { text: r.eticheta }), icon('chevronRight', 11)]));
      }
    }

    corp.append(
      el('div', { class: 'mt-meniu-titlu', text: 'Вид' }),
      el('button', {
        class: 'mt-meniu-rand',
        onclick: async () => {
          inchide();
          (await import('./theme-picker.js')).deschideTeme();
        },
      }, [icon('palette', 15), el('span', { text: 'Тема оформления' }), icon('chevronRight', 11)]),
      el('button', { class: 'mt-meniu-rand', onclick: () => alegeVersiune('desktop') },
        [icon('window', 15), el('span', { text: 'Полная версия сайта' }), icon('chevronRight', 11)]),
      el('button', {
        class: 'mt-meniu-rand',
        onclick: async () => {
          // Intai inchidem foaia, apoi iesim: altfel meniul ramane deschis peste
          // ecranul de intrare, cu randuri care nu mai duc nicaieri.
          inchide();
          await session.logout();
        },
      }, [icon('logout', 15), el('span', { text: 'Выйти' })]),
    );

    // Cu titlu, nu fara: titlul aduce cu el butonul de inchidere. Fara el, singura
    // iesire ramane gestul in jos — pe care nu-l stie toata lumea.
    ({ inchide } = foaie({ titlu: 'Меню', corp, inalta: true }));
  }

  /** Titlul paginii curente, luat din tabelul de rute. */
  aplicaRuta(drum, eticheta) {
    this.setTitle(eticheta || 'ScalpScreener');
    this.setActive(drum);
    this.root.classList.remove('ascunsa');
    this.viewEl.scrollTop = 0;
  }
}
